<template>
  <div
    v-if="mounted"
    id="cookie-notice"
    ref="bannerEl"
    role="region"
    :aria-label="$t('cookieNotice.message')"
    class="fixed bottom-0 inset-x-0 z-50 bg-primary text-primary-foreground shadow-lg transition-opacity duration-500"
    :style="bannerStyle"
  >
    <div
      class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-3"
    >
      <p class="text-sm text-center sm:text-left">
        {{ $t('cookieNotice.message') }}
        <RouterLink :to="learnMoreTarget" class="underline hover:no-underline">
          {{ $t('cookieNotice.learnMore') }}
        </RouterLink>
      </p>
      <button
        type="button"
        class="shrink-0 bg-primary-foreground text-primary text-sm font-medium px-4 py-2 rounded hover:bg-primary-foreground/90"
        @click="dismiss"
      >
        {{ $t('cookieNotice.button') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useLocalePath } from '../composables/useLocalePath'

const STORAGE_KEY = 'haiafara:cookie-notice-ack'
const APPEAR_DELAY_MS = 2000

const { localePath } = useLocalePath()

const mounted = ref(false)
const visible = ref(false)
const overlap = ref(0)
const bannerEl = ref(null)
let paddedEl = null
let ticking = false
let layoutObserver = null
let appearTimer = null

const learnMoreTarget = computed(() => ({
  ...localePath('Privacy'),
  hash: '#cookies',
}))

const bannerStyle = computed(() => {
  const style = { opacity: visible.value ? '1' : '0' }
  if (overlap.value > 0) style.transform = `translateY(-${overlap.value}px)`
  return style
})

function applyPadding() {
  if (!bannerEl.value) return
  const footer = document.querySelector('footer')
  const target = (footer && footer.previousElementSibling) || document.body
  if (paddedEl && paddedEl !== target) paddedEl.style.paddingBottom = ''
  target.style.paddingBottom = `${bannerEl.value.offsetHeight}px`
  paddedEl = target
}

function clearPadding() {
  if (!paddedEl) {
    return
  }

  paddedEl.style.paddingBottom = ''
  paddedEl = null
}

function updatePosition() {
  ticking = false
  if (!bannerEl.value) {
    overlap.value = 0
    return
  }
  const footer = document.querySelector('footer')
  if (!footer) {
    overlap.value = 0
    return
  }
  const footerTop = footer.getBoundingClientRect().top
  overlap.value = Math.max(0, innerHeight - footerTop)
}

function onScroll() {
  if (ticking) {
    return
  }

  requestAnimationFrame(updatePosition)
  ticking = true
}

function onResize() {
  applyPadding()
  onScroll()
}

function observeLayout() {
  if (!('ResizeObserver' in globalThis)) return
  layoutObserver = new ResizeObserver(() => {
    applyPadding()
    onScroll()
  })
  layoutObserver.observe(document.documentElement)
  const footer = document.querySelector('footer')
  if (footer) layoutObserver.observe(footer)
}

function disconnectLayoutObserver() {
  if (!layoutObserver) {
    return
  }

  layoutObserver.disconnect()
  layoutObserver = null
}

function dismiss() {
  try {
    localStorage.setItem(STORAGE_KEY, '1')
  } catch {
    // localStorage unavailable; dismissal stays in-memory for this session.
  }
  visible.value = false
  setTimeout(() => {
    mounted.value = false
    clearPadding()
  }, 500)
  disconnectLayoutObserver()
  removeEventListener('scroll', onScroll)
  removeEventListener('resize', onResize)
}

onMounted(() => {
  try {
    if (!globalThis.localStorage || localStorage.getItem(STORAGE_KEY)) return
  } catch {
    return
  }
  appearTimer = setTimeout(async () => {
    appearTimer = null
    mounted.value = true
    await nextTick()
    // Apply padding + re-measure across several frames so the footer has
    // time to reflow after we grow the content area. Without this, on pages
    // where the footer is already in view, updatePosition can read a stale
    // footerTop and the banner ends up covering the footer.
    for (let i = 0; i < 3; i++) {
      applyPadding()
      await new Promise((r) => requestAnimationFrame(r))
      updatePosition()
    }
    observeLayout()
    addEventListener('scroll', onScroll, { passive: true })
    addEventListener('resize', onResize, { passive: true })
    requestAnimationFrame(() => {
      visible.value = true
    })
  }, APPEAR_DELAY_MS)
})

onBeforeUnmount(() => {
  if (appearTimer) {
    clearTimeout(appearTimer)
    appearTimer = null
  }
  clearPadding()
  disconnectLayoutObserver()
  removeEventListener('scroll', onScroll)
  removeEventListener('resize', onResize)
})
</script>
